import { Venue, City, Therapy, Article, NearbyVenueSearchResponse, ProgrammaticPageResponse } from '../types';
import { filterPublicDirectoryTherapies } from '../lib/publicTherapies';

const THERAPY_IMAGE_FALLBACKS: Record<string, string> = {
  sauna: '/images/Sauna.webp',
  'ice-bath': '/images/ice-bath.webp',
  cryotherapy: '/images/cryo.jpg',
  hbot: '/images/hbot-new.avif',
  'red-light': '/images/redlight.webp',
  'float-therapy': '/images/float-therapy.jpg',
  'contrast-therapy': '/images/sauna-hero-optimised.jpg',
};

let citiesCache: City[] | null = null;
let citiesPromise: Promise<City[]> | null = null;
let therapiesCache: Therapy[] | null = null;
let therapiesPromise: Promise<Therapy[]> | null = null;
const venuesCache = new Map<string, Venue[]>();
const venuePromises = new Map<string, Promise<Venue[]>>();

export type NewsletterSignupInput = {
  email: string;
  name?: string;
  location?: string;
  ageRange?: string;
  goals?: string[];
  interests?: string[];
  source?: string;
};

export type ClaimRequestInput = {
  listingIntent: 'claim' | 'submit_new';
  businessName: string;
  businessUrl: string;
  email: string;
};

export type PostcodeSearchLogInput = {
  postcode: string;
  radiusMiles: number;
  therapy?: string;
  source: 'homepage';
  resultsCount?: number;
};

export const api = {
  async getFeaturedVenues(): Promise<Venue[]> {
    const res = await fetch('/api/featured-venues');
    return res.json();
  },

  async getCities(): Promise<City[]> {
    if (citiesCache) return citiesCache;
    if (citiesPromise) return citiesPromise;

    citiesPromise = fetch('/api/cities')
      .then((res) => res.json())
      .then((data) => {
        citiesCache = Array.isArray(data) ? data : [];
        return citiesCache;
      })
      .finally(() => {
        citiesPromise = null;
      });

    return citiesPromise;
  },

  async getTherapies(): Promise<Therapy[]> {
    if (therapiesCache) return therapiesCache;
    if (therapiesPromise) return therapiesPromise;

    therapiesPromise = fetch('/api/therapies')
      .then((res) => res.json())
      .then((data) => {
        if (!Array.isArray(data)) {
          therapiesCache = [];
          return therapiesCache;
        }

        therapiesCache = filterPublicDirectoryTherapies(data.map((therapy: Therapy) => ({
          ...therapy,
          image: THERAPY_IMAGE_FALLBACKS[therapy.slug] ?? therapy.image,
        })));

        return therapiesCache;
      })
      .finally(() => {
        therapiesPromise = null;
      });

    return therapiesPromise;
  },

  async getVenues(city?: string, therapy?: string): Promise<Venue[]> {
    const params = new URLSearchParams();
    if (city) params.append('city', city);
    if (therapy) params.append('therapy', therapy);
    const key = params.toString();

    if (venuesCache.has(key)) return venuesCache.get(key) ?? [];
    if (venuePromises.has(key)) return venuePromises.get(key)!;

    const request = fetch(`/api/venues?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        const normalized = Array.isArray(data) ? data : [];
        venuesCache.set(key, normalized);
        return normalized;
      })
      .finally(() => {
        venuePromises.delete(key);
      });

    venuePromises.set(key, request);
    return request;
  },

  async getVenueBySlug(slug: string): Promise<Venue> {
    const res = await fetch(`/api/venues/${slug}`);
    if (!res.ok) throw new Error('Venue not found');
    return res.json();
  },

  async getArticles(type: 'journal' | 'best_of' | 'all' = 'journal'): Promise<Article[]> {
    const params = new URLSearchParams({ type });
    const res = await fetch(`/api/articles?${params.toString()}`);
    return res.json();
  },

  async getArticleById(id: string): Promise<Article> {
    const res = await fetch(`/api/articles/${id}`);
    if (!res.ok) throw new Error('Article not found');
    return res.json();
  },

  async getEditorialBySlug(slug: string): Promise<Article> {
    const res = await fetch(`/api/editorial/${slug}`);
    if (!res.ok) throw new Error('Editorial not found');
    return res.json();
  },

  async getProgrammaticPageBySlug(slug: string): Promise<ProgrammaticPageResponse> {
    const res = await fetch(`/api/programmatic/${slug}`);
    if (!res.ok) throw new Error('Programmatic page not found');
    return res.json();
  },

  async getNearbyVenues(postcode: string, radiusMiles: number, therapy?: string): Promise<NearbyVenueSearchResponse> {
    const params = new URLSearchParams({
      postcode,
      radius: String(radiusMiles),
    });
    if (therapy && therapy !== 'all') params.set('therapy', therapy);

    const res = await fetch(`/api/venues/nearby?${params.toString()}`, { cache: 'no-store' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(typeof data?.error === 'string' ? data.error : 'Nearby venues not found');
    }

    return res.json();
  },

  async subscribeToNewsletter(input: string | NewsletterSignupInput): Promise<boolean> {
    const payload = typeof input === 'string' ? { email: input } : input;
    const res = await fetch('/api/newsletter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.ok;
  },

  async saveNewsletterPreferences(email: string, goals: string[], interests: string[]): Promise<boolean> {
    const res = await fetch('/api/newsletter/preferences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, goals, interests }),
    });
    return res.ok;
  },

  async submitClaimRequest(input: ClaimRequestInput): Promise<boolean> {
    const res = await fetch('/api/claim-requests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return res.ok;
  },

  async logPostcodeSearch(input: PostcodeSearchLogInput): Promise<boolean> {
    const res = await fetch('/api/postcode-searches', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
      keepalive: true,
    });
    return res.ok;
  },
};
